import { useState, useCallback } from 'react';
import {
    ListGroup, ListGroupItem, Spinner,
} from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar as faStarSolid, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarRegular } from '@fortawesome/free-regular-svg-icons';
import { setUser } from '@s20/slices/features/common.slice';
import { getAllTenants, getTenant, getUser } from '@s20/selectors/features/common.selectors';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes } from '../../Common';
import { IconButton, SmallButton, toasty } from '../../common/forms/FormElements';
import { Alert } from '../../common/Modal';
import { useSwitchTenant } from '../../hooks/useSwitchTenant';
import { Logo } from './Logo';
import './index.scss';

const TenantAccessModal = () => {
    const tenants = useSelector(getAllTenants);

    const [loadingTenants, setLoadingTenants] = useState(null);
    const [settingDefault, setSettingDefault] = useState(null);

    const user = useSelector(getUser);
    const tenant = useSelector(getTenant);

    const dispatch = useDispatch();
    const changeTenantCallback = useSwitchTenant();

    const defaultTenantId = user?.tenantId ?? null;

    const handleTenantClick = useCallback(async (tenantId) => {
        setLoadingTenants(tenantId);

        await changeTenantCallback(tenantId)();

        setLoadingTenants(null);
        Alert.hide();
    }, [changeTenantCallback]);

    const handleSetDefault = useMutation({
        mutationFn: async (tenantId) => {
            setSettingDefault(tenantId);

            await httpClient.put(ApiRoutes.auth.setDefaultTenant(tenantId), {}, httpClient.format.json);

            return tenantId;
        },
        onSuccess: (tenantId) => {
            if (user) {
                dispatch(setUser({ ...user, tenantId }));
            }

            toasty.success('Default tenant updated.');
            setSettingDefault(null);
        },
        onError: () => {
            toasty.error('Failed to update default tenant.');
            setSettingDefault(null);
        },
    });

    return (
        <div>
            <ListGroup className="tenant-list-group">
                {tenants.map((t) => {
                    const isCurrentSession = tenant?.id === t.id;
                    const isDefault = defaultTenantId === t.id;

                    return (
                        <ListGroupItem
                            key={t.id}
                            className="d-flex align-items-center px-3 py-2"
                        >
                            <Logo
                                alt={t.companyName}
                                src={ApiRoutes.tenant.getTenantLogo(t.id)}
                            />

                            <SmallButton
                                color="link"
                                className="w-auto p-0 text-left flex-grow-1"
                                onClick={() => !isCurrentSession && handleTenantClick(t.id)}
                                disabled={isCurrentSession}
                            >
                                {t.companyName}

                                {loadingTenants === t.id && (
                                    <Spinner
                                        size="sm"
                                        className="ml-2"
                                    />
                                )}
                            </SmallButton>


                            <IconButton
                                className="tenant-default-button mr-2"
                                disabled={isDefault || settingDefault === t.id}
                                onClick={() => handleSetDefault.mutateAsync(t.id)}
                                title={isDefault ? 'Default tenant' : 'Set as default tenant'}
                            >
                                {settingDefault === t.id ? (
                                    <Spinner size="sm" />
                                ) : (
                                    <FontAwesomeIcon
                                        icon={isDefault ? faStarSolid : faStarRegular}
                                        className={isDefault ? 'text-warning' : ''}
                                    />
                                )}
                            </IconButton>

                            <FontAwesomeIcon
                                icon={faChevronRight}
                                className={`text-muted ${isCurrentSession ? '' : 'tenant-list-item-clickable'}`}
                                onClick={() => !isCurrentSession && handleTenantClick(t.id)}
                            />
                        </ListGroupItem>
                    );
                })}
            </ListGroup>
        </div>
    );
};

export default TenantAccessModal;
